'use client';

/**
 * Stable DOM identity for every rendered block — the hook layer behind
 * these attributes on the block's root element:
 *
 * <ul>
 *   <li>{@code data-<ns>-group} — block family ({@code hero} / {@code rail}
 *       / …), from the source taxonomy. "select every hero block".</li>
 *   <li>{@code data-<ns>-block} — block type ({@code CourseRow}). "select
 *       every course row".</li>
 *   <li>{@code data-<ns>-id} — <b>unique</b> per instance
 *       ({@code CourseRow-popular} / {@code CourseRow-2}). "select this
 *       one". See {@code blockId.ts} for how it's derived.</li>
 *   <li>{@code data-<ns>-anchor} — the operator-authored slug, when set.
 *       The stable target for onboarding + pinned tests.</li>
 *   <li>{@code data-<ns>-part} / {@code data-<ns>-index} — a meaningful
 *       element INSIDE a block (see {@link useBlockPart}).</li>
 * </ul>
 *
 * <p>Used by test automation (Playwright / Cypress) and product
 * onboarding (driver.js / shepherd / react-joyride).</p>
 *
 * <h3>No extra DOM</h3>
 *
 * The attributes land on the element the block <em>already</em> renders.
 * {@code RenderOneBlock} puts the identity in context around every block
 * (page-level and nested); {@link SectionShell} and {@link BlockRoot}
 * read it via {@link useBlockRootAttrs} and spread it onto their root
 * element. A block with a bespoke root calls {@link useBlockRootAttrs}
 * itself. Nothing threads through block prop signatures.
 *
 * <h3>Namespace</h3>
 *
 * The {@code data-<ns>-*} prefix defaults to {@link DEFAULT_BLOCK_ATTR_NS}
 * ({@code "gbt"}) and is overridable from a single place — the host
 * mounts {@link BlockAttrConfigProvider} near the app root and can wire
 * it to an env var / tenant / account-config value. Every hook here
 * resolves the namespace through that context, so changing it is one
 * provider prop, not a find-and-replace across block files.
 *
 * <p>{@code 'use client'}: {@code createContext} is client-only. The
 * whole block render tree is already inside a client boundary
 * ({@code RenderOneBlock} / {@code ComposedTreeRenderer} are
 * {@code 'use client'}), so consuming these hooks adds no new boundary.
 */

import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { slugify } from './blockId';

/** Default {@code data-<ns>-*} attribute namespace. */
export const DEFAULT_BLOCK_ATTR_NS = 'gbt';

/** Resolved config for the {@code data-<ns>-*} attribute family. */
export interface BlockAttrConfig {
  /** Prefix segment — {@code "gbt"} → {@code data-gbt-block}. */
  namespace: string;
}

const BlockAttrConfigContext = createContext<BlockAttrConfig>({
  namespace: DEFAULT_BLOCK_ATTR_NS,
});

/**
 * Override the {@code data-<ns>-*} prefix for the wrapped tree. Mount
 * once near the app root. Pass {@code namespace} from wherever the host
 * wants it to come from (a {@code NEXT_PUBLIC_*} env var, the resolved
 * account config, a build flag…). Omit / blank → {@link DEFAULT_BLOCK_ATTR_NS}.
 */
export function BlockAttrConfigProvider({
  namespace,
  children,
}: {
  namespace?: string | null;
  children: React.ReactNode;
}) {
  const resolved = (namespace ?? '').trim() || DEFAULT_BLOCK_ATTR_NS;
  return (
    <BlockAttrConfigContext.Provider value={{ namespace: resolved }}>
      {children}
    </BlockAttrConfigContext.Provider>
  );
}

/** Resolved attribute namespace for the current tree (default {@code "gbt"}). */
export function useBlockAttrNamespace(): string {
  return useContext(BlockAttrConfigContext).namespace;
}

/**
 * Identity of the block instance currently rendering. Set by
 * {@code RenderOneBlock} around every block — page-level slots and
 * nested container slots alike. {@code null} when a block renders
 * outside the portal pipeline (Storybook, unit tests); the attribute
 * hooks then return {@code {}} so nothing breaks.
 */
export interface BlockIdentity {
  /** Block type id, e.g. {@code "HeroSplit"} → {@code data-<ns>-block}. */
  type: string;
  /**
   * Unique per-instance id → {@code data-<ns>-id}. Deterministic:
   * operator anchor → payload node id → a readable content key
   * ({@code CourseRow-popular}) → positional ({@code CourseRow-2}).
   * Computed by the tree walker (see {@code blockId.ts}) and threaded in
   * as the reserved {@code props._gbtId}. Falls back to {@link type} when
   * a block renders outside that walker.
   */
  id?: string;
  /**
   * Block family from the source taxonomy ({@code hero} / {@code rail} /
   * {@code cta} / …) → {@code data-<ns>-group}. Threaded in as the
   * reserved {@code props._gbtGroup}.
   */
  group?: string;
  /** Operator-authored anchor / onboarding slug — reserved
   *  {@code props._anchor}. Present only when the operator set one;
   *  emitted as its own {@code data-<ns>-anchor} so onboarding code can
   *  target "operator promised this is stable" instances specifically. */
  anchor?: string;
}

export const BlockIdentityContext = createContext<BlockIdentity | null>(null);

/** The current block's identity, or {@code null} outside the renderer. */
export function useBlockIdentity(): BlockIdentity | null {
  return useContext(BlockIdentityContext);
}

/**
 * True once a {@code data-<ns>-*} root has been stamped for the current
 * block — set by {@link BlockRoot}, {@link SectionShell} and {@code
 * AkSection} around their children. A block whose root wrapper nests
 * inside another ({@code <AkSection><BlockRoot as="aside">…}) would
 * otherwise stamp the identity twice, so {@link useBlockRootAttrs}
 * returns {@code {}} for every call below the first.
 */
export const BlockRootStampedContext = createContext(false);

/**
 * Marks the subtree as "root already stamped". Wrap a root wrapper's
 * children in it so a nested wrapper doesn't restamp {@code data-<ns>-block}.
 */
export function BlockRootStamped({ children }: { children: React.ReactNode }) {
  return (
    <BlockRootStampedContext.Provider value={true}>{children}</BlockRootStampedContext.Provider>
  );
}

/**
 * A {@code data-*}-keyed attribute bag — spreadable straight onto a JSX
 * host element. The index signature is the {@code data-} template so it
 * type-checks against intrinsic element props.
 */
export interface BlockDataAttrs {
  [attr: `data-${string}`]: string | undefined;
}

/**
 * Attributes for a block's ROOT element. Spread onto the element the
 * block already renders — no extra node.
 *
 * ```tsx
 * const rootAttrs = useBlockRootAttrs();
 * return <section {...rootAttrs} style={…}>…</section>;
 * ```
 *
 * {@link SectionShell} and {@link BlockRoot} already do this, so most
 * blocks get it for free.
 */
export function useBlockRootAttrs(): BlockDataAttrs {
  const ns = useBlockAttrNamespace();
  const identity = useBlockIdentity();
  const alreadyStamped = useContext(BlockRootStampedContext);
  const attrs: BlockDataAttrs = {};
  if (!identity || alreadyStamped) return attrs;
  if (identity.group) attrs[`data-${ns}-group`] = identity.group;
  attrs[`data-${ns}-block`] = identity.type;
  attrs[`data-${ns}-id`] = identity.id || identity.type;
  if (identity.anchor) attrs[`data-${ns}-anchor`] = identity.anchor;
  return attrs;
}

/**
 * Canonical {@code data-<ns>-part} role names. A shared vocabulary so a
 * test / tour written against one block transfers to the next: every
 * "see all" link is {@code part('action')}, every repeated card is
 * {@code part('item', i)}, and so on. Blocks MAY coin a bespoke name
 * for something genuinely block-specific, but reach for one of these
 * first.
 *
 * <ul>
 *   <li>{@code header} — the title/action row container</li>
 *   <li>{@code kicker} — eyebrow / label above the title</li>
 *   <li>{@code title} — the block's main heading</li>
 *   <li>{@code subtitle} — supporting line under the title</li>
 *   <li>{@code action} — header-level action, usually "see all" / "view all"</li>
 *   <li>{@code body} — main content region, when it must be told apart from the header</li>
 *   <li>{@code list} — a repeated-items container (rail track, grid)</li>
 *   <li>{@code item} — one repeated item; pass the index</li>
 *   <li>{@code media} — image / video / thumbnail of an item or hero</li>
 *   <li>{@code cta} — primary call-to-action; {@code cta-secondary} for a second</li>
 *   <li>{@code search} — a search input</li>
 *   <li>{@code filter} — a filter control (pills, dropdown)</li>
 *   <li>{@code tablist} / {@code tab} / {@code panel} — tabbed UI</li>
 *   <li>{@code pager} — pagination control</li>
 *   <li>{@code empty} — the empty-state region</li>
 *   <li>{@code field-<name>} — a named form input ({@code field-email})</li>
 * </ul>
 */
export const BLOCK_PARTS = [
  'header',
  'kicker',
  'title',
  'subtitle',
  'action',
  'body',
  'list',
  'item',
  'media',
  'cta',
  'cta-secondary',
  'search',
  'filter',
  'tablist',
  'tab',
  'panel',
  'pager',
  'empty',
] as const;

/** One of the {@link BLOCK_PARTS} names, or any bespoke kebab-case role. */
export type BlockPartName = (typeof BLOCK_PARTS)[number] | (string & {});

/**
 * Builder for attributes on a meaningful element INSIDE a block (its
 * title, primary CTA, a repeated item…). Returns a stable function so
 * it is safe to call once per block and reuse for every part, including
 * inside {@code .map()} where a hook call is not allowed.
 *
 * ```tsx
 * const part = useBlockPart();
 * return (
 *   <section {...useBlockRootAttrs()}>
 *     <h2 {...part('title')}>{title}</h2>
 *     <a {...part('cta')} href={href}>{cta}</a>
 *     <ul>{items.map((it, i) => <li key={i} {...part('item', i)}>{it.label}</li>)}</ul>
 *   </section>
 * );
 * ```
 *
 * {@code name} is a kebab-case role — prefer one of {@link BLOCK_PARTS};
 * {@code index} disambiguates repeated items.
 *
 * <p>Outside the portal renderer (Storybook, unit tests) there is no
 * block identity in context and the builder is a no-op — it returns
 * {@code {}} so a shared primitive doesn't scatter stray
 * {@code data-<ns>-part} attributes where there is no block root to
 * anchor them to. Same contract as {@link useBlockRootAttrs}.
 */
export function useBlockPart(): (name: BlockPartName, index?: number) => BlockDataAttrs {
  const ns = useBlockAttrNamespace();
  const hasIdentity = useBlockIdentity() != null;
  return useCallback(
    (name: BlockPartName, index?: number) => {
      if (!hasIdentity) return {};
      const attrs: BlockDataAttrs = { [`data-${ns}-part`]: name };
      if (index != null) attrs[`data-${ns}-index`] = String(index);
      return attrs;
    },
    [ns, hasIdentity],
  );
}

/**
 * Every interactive element auto-stamped by {@link useBlockControlStamp}:
 * links, buttons, form fields and their ARIA-role equivalents. Kept in
 * sync with what a test / tour would ever want to click or type into.
 */
const INTERACTIVE_SELECTOR =
  'a[href],button,input:not([type="hidden"]),select,textarea,summary,' +
  '[role="button"],[role="link"],[role="tab"],[role="menuitem"],' +
  '[role="checkbox"],[role="radio"],[role="switch"],[role="option"]';

/** The raw accessible name an element's {@code data-<ns>-el} handle derives from. */
function controlName(el: Element): string {
  const named = el as HTMLElement;
  const value =
    el instanceof HTMLInputElement && el.type !== 'password' && el.type !== 'search'
      ? el.value
      : '';
  return (
    named.getAttribute('aria-label') ||
    named.getAttribute('title') ||
    named.getAttribute('placeholder') ||
    value ||
    named.textContent ||
    ''
  );
}

/**
 * Pure core of {@link useBlockControlStamp}: turn an ordered list of
 * control accessible-names into their {@code data-<ns>-el} handles —
 * slugified, positional {@code c<n>} fallback for the unnamed, and a
 * {@code -2}/{@code -3} suffix on duplicates. Exported for tests.
 */
export function assignControlHandles(names: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((raw, i) => {
    const base = slugify(raw).slice(0, 40) || `c${i}`;
    const dup = seen.get(base) ?? 0;
    seen.set(base, dup + 1);
    return dup > 0 ? `${base}-${dup + 1}` : base;
  });
}

/**
 * The panel ids a trigger owns — its {@code aria-controls} +
 * {@code aria-owns} token lists. A Radix {@code Select} / {@code Popover}
 * / {@code DropdownMenu} trigger points here at its content, which is
 * portalled to {@code <body>} — out of the block's DOM subtree. Pure;
 * exported for tests.
 */
export function ownedPanelIds(el: Element): string[] {
  const raw = `${el.getAttribute('aria-controls') ?? ''} ${el.getAttribute('aria-owns') ?? ''}`;
  return raw.split(/\s+/).filter(Boolean);
}

/**
 * Stamp {@code data-<ns>-el} onto every interactive control under
 * {@code root} that isn't claimed by a nested block — plus the controls
 * of any popup panel a trigger in this block owns via
 * {@code aria-controls} / {@code aria-owns} (Radix overlays portal their
 * content out of the subtree). Shared by both variants of
 * {@link useBlockControlStamp}.
 */
function stampControlsUnder(root: Element, ns: string): void {
  const blockSel = `[data-${ns}-block]`;
  const elAttr = `data-${ns}-el`;
  const doc = root.ownerDocument;
  const own: Element[] = [];

  // Skip a control whose nearest block ancestor is a NESTED block (that
  // block stamps its own). A control with no block ancestor, or one
  // whose nearest is `root` itself, belongs here.
  const isNested = (node: Element) => {
    const owner = node.closest(blockSel);
    return !!owner && owner !== root && root.contains(owner);
  };

  root.querySelectorAll(INTERACTIVE_SELECTOR).forEach((node) => {
    if (!isNested(node)) own.push(node);
  });

  // Follow this block's triggers to their portalled panels.
  const seen = new Set<Element>();
  root.querySelectorAll('[aria-controls],[aria-owns]').forEach((trigger) => {
    if (isNested(trigger)) return;
    for (const id of ownedPanelIds(trigger)) {
      const panel = doc?.getElementById(id);
      if (!panel || root.contains(panel) || seen.has(panel)) continue;
      seen.add(panel);
      panel.querySelectorAll(INTERACTIVE_SELECTOR).forEach((node) => own.push(node));
    }
  });

  const handles = assignControlHandles(own.map(controlName));
  own.forEach((node, i) => {
    if (node.getAttribute(elAttr) !== handles[i]) node.setAttribute(elAttr, handles[i]!);
  });
}

/**
 * Auto-stamp {@code data-<ns>-el} onto EVERY interactive element inside
 * the block — every {@code <button>}, {@code <a href>}, form field and
 * ARIA-role control — that {@link useBlockPart} did not already name.
 * This closes the gap where a test or onboarding step needs to reach a
 * control the block author never tagged.
 *
 * <p>The value is the control's slugified accessible name
 * ({@code enroll-now}, {@code see-all}, {@code next}), or {@code c<n>}
 * positional when it has no text; duplicates within one block get
 * {@code -2}, {@code -3}. Controls that belong to a <em>nested</em> block
 * are left for that block's own pass.
 *
 * <p>Runs after hydration (client only, effect) and re-runs through a
 * {@code MutationObserver} when controls are added or removed (a list
 * pages in) or when a trigger's {@code aria-expanded} /
 * {@code aria-controls} flips (a Select / Popover / menu panel opens).
 * The SSR markup stays clean.
 *
 * <p>Wired into {@link BlockRoot}, {@code SectionShell} (blocks package)
 * and {@code AkSection} (akademi-net) — every block built on them gets it
 * for free. A block with a bespoke root can attach the returned ref to
 * that element itself. No-op below the first root wrapper (same
 * nested-root guard as {@link useBlockRootAttrs}) and outside the
 * renderer.
 *
 * <p>{@code options.portal}: pass {@code true} when attaching the ref to
 * a surface rendered OUT of the block's DOM subtree — a modal / overlay
 * mounted via {@code createPortal} to {@code document.body}. Such a
 * surface is never reached by an ancestor wrapper's observer, so it opts
 * in explicitly and the nested-root guard is skipped (there's no
 * double-stamp risk — the DOM trees are disjoint).
 */
export function useBlockControlStamp<T extends HTMLElement = HTMLElement>(options?: {
  portal?: boolean;
}) {
  const portal = options?.portal ?? false;
  const ns = useBlockAttrNamespace();
  const hasIdentity = useBlockIdentity() != null;
  const nestedRoot = useContext(BlockRootStampedContext);
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!hasIdentity || (nestedRoot && !portal) || !root || typeof MutationObserver === 'undefined')
      return;

    const stamp = () => stampControlsUnder(root, ns);
    stamp();

    const schedule =
      typeof requestAnimationFrame === 'function' ? requestAnimationFrame : setTimeout;
    const unschedule =
      typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout;
    let pending: number | ReturnType<typeof setTimeout> = 0;
    const obs = new MutationObserver(() => {
      unschedule(pending as number);
      pending = schedule(stamp);
    });
    // `attributeFilter` is disjoint from what `stamp()` writes
    // (`data-<ns>-el`), so stamping never re-triggers the observer.
    obs.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'aria-controls', 'aria-owns'],
    });

    return () => {
      unschedule(pending as number);
      obs.disconnect();
    };
  }, [ns, hasIdentity, nestedRoot, portal]);

  return ref;
}
